/**
 * Web-Push subscription helper.
 *
 * Usage:
 *   import { registerPushSubscription, unsubscribePush, getPushStatus } from "./lib/webPush";
 *
 *   const status = await getPushStatus();    // { supported, permission, subscribed }
 *   await registerPushSubscription();        // prompts permission + subscribes
 *   await unsubscribePush();                 // tears down subscription + tells backend
 *
 * The Service Worker file lives at /sw.js (public/sw.js). The VAPID public key
 * is pulled from GET /api/push/public-key.
 */
import api from "./api";

const SW_PATH = "/sw.js";

function isSupported() {
    return typeof window !== "undefined"
        && "serviceWorker" in navigator
        && "PushManager" in window
        && "Notification" in window;
}

function urlBase64ToUint8Array(b64) {
    const padding = "=".repeat((4 - (b64.length % 4)) % 4);
    const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(base64);
    return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

async function getRegistration() {
    const existing = await navigator.serviceWorker.getRegistration(SW_PATH);
    if (existing) return existing;
    return navigator.serviceWorker.register(SW_PATH, { scope: "/" });
}

export async function getPushStatus() {
    if (!isSupported()) {
        return { supported: false, permission: "unsupported", subscribed: false };
    }
    try {
        const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        return {
            supported: true,
            permission: Notification.permission,
            subscribed: !!sub,
            endpoint: sub?.endpoint || null,
        };
    } catch {
        return { supported: true, permission: Notification.permission, subscribed: false };
    }
}

export async function registerPushSubscription() {
    if (!isSupported()) return { ok: false, reason: "unsupported" };
    try {
        const perm = await Notification.requestPermission();
        if (perm !== "granted") return { ok: false, reason: perm };

        const { data } = await api.get("/push/public-key");
        if (!data?.public_key) return { ok: false, reason: "vapid_not_configured" };

        const registration = await getRegistration();
        await navigator.serviceWorker.ready;

        // Reuse existing subscription if already there
        let subscription = await registration.pushManager.getSubscription();
        if (!subscription) {
            subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(data.public_key),
            });
        }

        const subJson = subscription.toJSON();
        await api.post("/push/subscribe", {
            endpoint: subJson.endpoint,
            keys: { p256dh: subJson.keys.p256dh, auth: subJson.keys.auth },
            user_agent: navigator.userAgent.slice(0, 300),
        });
        return { ok: true, endpoint: subJson.endpoint };
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("registerPushSubscription failed:", err);
        return { ok: false, reason: "exception", error: String(err) };
    }
}

export async function unsubscribePush() {
    if (!isSupported()) return { ok: false, reason: "unsupported" };
    try {
        const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        if (!sub) return { ok: true, alreadyUnsubscribed: true };

        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        try {
            await api.delete("/push/subscribe", { data: { endpoint } });
        } catch { /* backend cleanup is best-effort */ }
        return { ok: true };
    } catch (err) {
        return { ok: false, reason: "exception", error: String(err) };
    }
}

export async function updatePushCategories(disabledCategories) {
    const status = await getPushStatus();
    if (!status.subscribed || !status.endpoint) return { ok: false, reason: "not_subscribed" };
    await api.patch("/push/categories", {
        endpoint: status.endpoint,
        disabled_categories: disabledCategories,
    });
    return { ok: true };
}
